// Schema-driven section form — renders inputs from the studio template's page
// definition (fields, types, record-table config). Section titles/labels are
// NEVER hardcoded here; renaming in the admin studio flows straight through.
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import PhotoField from "./PhotoField";

function Head({ ml, en, hint }) {
  return (
    <div className="mb-4">
      <h3 className="text-lg font-bold" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>{ml}</h3>
      <p className="text-xs text-muted-foreground">{en}{hint ? ` · ${hint}` : ""}</p>
    </div>
  );
}

function F({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

const fieldsOf = (pageDef) => (pageDef.config?.fields || []).filter(f => f.enabled !== false);

/* ---- generic object-section (cover / note / details / back_cover) ---- */
function ObjectFields({ pageDef, data, onChange, testid }) {
  const set = (k, v) => onChange({ ...(data || {}), [k]: v });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {fieldsOf(pageDef).map(f => {
        const wide = f.type === "textarea";
        const input = f.type === "textarea" ? (
          <Textarea rows={3} value={data?.[f.key] || ""} placeholder={f.placeholder || ""}
            onChange={e => set(f.key, e.target.value)} data-testid={`${testid}-${f.key}`} />
        ) : f.type === "photo" || f.type === "signature" ? (
          <PhotoField value={data?.[f.key] || ""} onChange={v => set(f.key, v)} label={f.label}
            aspect={f.type === "signature" ? 3 / 1 : 4 / 5} testid={`${testid}-${f.key}`} />
        ) : (
          <Input type={f.type === "number" ? "number" : "text"} value={data?.[f.key] || ""} placeholder={f.placeholder || ""}
            onChange={e => set(f.key, e.target.value)} data-testid={`${testid}-${f.key}`} />
        );
        return (
          <div key={f.key} className={wide ? "sm:col-span-2" : ""}>
            <F label={f.label} testid={`${testid}-field-${f.key}`}>{input}</F>
          </div>
        );
      })}
    </div>
  );
}

/* ---- repeating record table (committee / staff / officers / custom) ---- */
function RecordTableForm({ pageDef, rows, onRows, testid }) {
  rows = Array.isArray(rows) ? rows : [];
  const cfg = pageDef.config || {};
  const fields = fieldsOf(pageDef);
  const setRow = (i, k, v) => onRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const blankRow = () => Object.fromEntries(fields.map(f => [f.key, ""]).concat([["photo", ""]]));
  const generate = (n) => {
    const count = Math.max(0, Math.min(100, parseInt(n) || 0));
    const defaults = cfg.defaults || [];
    const out = [];
    for (let i = 0; i < count; i++) {
      out.push(rows[i] || { ...blankRow(), ward_no: String(i + 1), designation: defaults[i] || "Member" });
    }
    onRows(out);
  };
  return (
    <div>
      <div className="flex items-end gap-2 mb-4 flex-wrap">
        {cfg.count_generator && (
          <F label="Number of Records">
            <Input type="number" min="0" max="100" value={rows.length} onChange={e => generate(e.target.value)} className="w-40" data-testid={`${testid}-count`} />
          </F>
        )}
        <Button type="button" variant="outline" size="sm" className="h-10" onClick={() => onRows([...rows, blankRow()])} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-lg border border-border p-3 bg-muted/20" data-testid={`${testid}-row-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground">#{i + 1}</span>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onRows(rows.filter((_, j) => j !== i))} data-testid={`${testid}-del-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {fields.map(f => (
                f.type === "photo" || f.type === "signature" ? null : (
                  <Input key={f.key} value={r[f.key] || ""} placeholder={f.label}
                    onChange={e => setRow(i, f.key, e.target.value)} className="bg-white" data-testid={`${testid}-${i}-${f.key}`} />
                )
              ))}
            </div>
            {cfg.photo && (
              <div className="mt-2">
                <PhotoField value={r.photo || ""} onChange={v => setRow(i, "photo", v)} label="Photo" aspect={1} testid={`${testid}-${i}-photo`} />
              </div>
            )}
          </div>
        ))}
        {!rows.length && <p className="text-sm text-muted-foreground text-center py-4" data-testid={`${testid}-empty`}>No records yet.</p>}
      </div>
    </div>
  );
}

/* ---- entry point ---- */
export default function SchemaSectionForm({ pageDef, data, onChange, user }) {
  const testid = `cc-page-${pageDef.id}`;
  const hintByKind = {
    note: "Malayalam / English / mixed text supported — type directly (മലയാളം)",
    details: "Master data auto-populated from your profile — fill only what's missing",
    record_table: "Unlimited records · photo optional (no photo → clean table row)",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <Head ml={pageDef.title_ml} en={pageDef.title_en} hint={hintByKind[pageDef.kind]} />
      {pageDef.kind === "details" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {[["Local Body", user?.local_body_name], ["Type", user?.local_body_type], ["District", user?.district], ["Block", user?.block_panchayat || "—"]].map(([k, v]) => (
            <div key={k} className="rounded-md bg-muted/50 border border-border px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
              <div className="text-sm font-semibold truncate">{v || "—"}</div>
            </div>
          ))}
        </div>
      )}
      {pageDef.kind === "record_table" ? (
        <RecordTableForm pageDef={pageDef} rows={data} onRows={onChange} testid={testid} />
      ) : (
        <ObjectFields pageDef={pageDef} data={data} onChange={onChange} testid={testid} />
      )}
    </Card>
  );
}
